<template>
  <div v-if="showExplore">
    <ExploreFiltersCard :organization-data="organizationData" />
  </div>
  <div class="no-margin-top d-flex pb-lg" :class="{ PageContainer: showExplore }">
    <div class="content w-100">
      <div v-if="Object.entries(selectedFilters.mappedFilters).length >= 0" class="py-xs">
        <ion-row class="ion-justify-content-between ion-align-items-center">
          <h3 id="result-text" class="h3">{{ pagination.count }} {{ t('search.results') }}</h3>
          <ion-spinner v-if="isLoading" name="crescent" color="primary"></ion-spinner>
        </ion-row>
      </div>

      <div v-if="pagination.count === 0 && !isLoading">
        <NoResultsCard />
        <ion-button
          @click="clearAllFilters()"
          :disabled="isLoading"
          fill="solid"
          expand="block"
          size="large"
          color="primary"
          class="text-lg text-weight-bold mt-lg w-100"
        >
          {{ t('search.loadMoreResults') }}
        </ion-button>
      </div>

      <ion-row class="ion-no-padding">
        <ion-col
          size="12"
          size-sm="12"
          size-md="6"
          size-lg="6"
          v-for="property in propertyInPage"
          :key="`property-${property.id}`"
          :class="
            Object.entries(selectedFilters.mappedFilters).length === 0 ? 'ion-padding-top' : ''
          "
        >
          <PropertyCard
            v-if="property.Images"
            :name="getCurrentNameProperty(property as Property, languageSelected) as string"
            :image="
              getCoverImage(
                property.Images?.length > 0 ? property.Images : (property.Images as Photos[]),
              )
            "
            :property="property"
            :hide-price="!user"
            :language="languageSelected"
          />
        </ion-col>
      </ion-row>
      <div v-if="propertyInPage.length > 0 && pagination.pages > pagination.page">
        <ion-button
          @click="loadMore"
          :disabled="isLoading"
          fill="solid"
          expand="block"
          size="default"
          color="primary"
          class="text-lg text-weight-bold mt-xs w-100"
        >
          {{ t('search.loadMoreResults') }}
        </ion-button>
      </div>
      <div
        v-else-if="pagination.pages === pagination.page"
        class="d-flex ion-justify-content-center w-100 py-lg flex-column align-center"
      >
        <p class="font-sm bold-medium">{{ t('search.cantFind') }}</p>
        <ion-button
          @click="clearAllFilters()"
          :disabled="isLoading"
          fill="solid"
          expand="block"
          size="large"
          color="primary"
          class="text-lg text-weight-bold mt-xs w-100"
        >
          {{ t('search.loadMoreResults') }}
        </ion-button>
      </div>
      <div v-if="isLoading" class="ion-padding-top rental-unit-list">
        <div v-for="card in 3" :key="card">
          <SkeletonLoaderCard :width="'100%'" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, watch } from 'vue';
  import ExploreFiltersCard from '@/components/ExploreFiltersCard.vue';
  import { IonRow, IonSpinner, IonButton, IonCol } from '@ionic/vue';
  import { useElasticStore } from '@/stores/elastic-store';
  import { storeToRefs } from 'pinia';
  import { useSearch } from '@/composables/useSearch';
  import { Photos, Property } from '@/interfaces/elastic/elastic.interface';
  import SkeletonLoaderCard from '@/components/loaders/SkeletonLoaderCard.vue';
  import { useRoute } from 'vue-router';
  import router from '@/router';
  import { useAuthStore } from '@/stores/auth-store';
  import { useI18n } from 'vue-i18n';
  import PropertyCard from '@/components/PropertyCard.vue';
  import { useFilters } from '@/composables/useFilters';
  import { useProfileStore } from '@/stores/profile-store';
  import NoResultsCard from './NoResultsCard.vue';

  withDefaults(
    defineProps<{
      organizationData?: any;
      showExplore?: boolean;
    }>(),
    {
      showExplore: true,
    },
  );

  const { t } = useI18n();
  const { pagination, propertyInPage, selectedFilters, isLoading } =
    storeToRefs(useElasticStore());
  const { getCurrentNameProperty, handleGetProperties, getCoverImage } = useSearch();
  const { clearFilters, setDefaultFilters } = useFilters();
  const route = useRoute();
  const { user } = storeToRefs(useAuthStore());

  const { languageSelected } = storeToRefs(useProfileStore());

  const loadMore = () => {
    handleGetProperties(pagination.value.page + 1);
  };

  const clearAllFilters = async (scroll: boolean = true) => {
    await clearFilters();
    await handleGetProperties();

    setTimeout(() => {
      const element = document.getElementById('result-text');
      if (element && scroll) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500);
  };

  const checkQuery = async () => {
    if (route.query.rentalId) {
      const rentalId = route.query.rentalId as string;
      await router.replace({ query: { rentalId: undefined } });
      await router.push({
        name: 'AnnouncementPage',
        params: { rentalId: rentalId },
      });
    }
  };

  const initialConfig = async () => {
    //
    await checkQuery();
    await setDefaultFilters();
    await handleGetProperties();
  };

  onMounted(() => {
    initialConfig();
  });

  watch(user, async () => {
    clearAllFilters(false);
  });

  watch(
    () => route.name,
    () => {
      if (route.name === 'MyPage') {
        checkQuery();
      }
    },
  );
</script>

<style lang="css" scoped>
  .rental-unit-list {
    gap: 20px;
    display: flex;
    flex-direction: column;
  }
</style>
